import React from 'react';

import { GridActionsCellItem } from '@mui/x-data-grid';

import { Divider, Popover } from '@mui/material';

import { MoreVert } from '@mui/icons-material';

import { AxiosError } from 'axios';

import { MenuItem } from 'components/Entities/EntityActionCell/styles';

import Logger from 'utils/logger';

import API from 'utils/api/api';
import { EntityModules } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import { ColumnActions } from 'components/Grid/types';
import { moreActionsLabel } from 'components/Grid/components/ActionCell';

import DeleteEntityModal from './DeletePipelineModal';

const PipelinesActionCell: React.FunctionComponent<{
  rowData: { uuid: string; name: string; isDefault: boolean };
  redirect: (uuid: string) => void;
  actions: ColumnActions;
}> = ({ rowData: { isDefault, uuid, name }, redirect, actions }) => {
  const { notification } = useStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  const handleClose = (): void => setAnchorEl(null);

  const handleEditClick = (event: React.MouseEvent<HTMLLIElement>): void => {
    setAnchorEl(event.currentTarget);
    redirect(uuid);
  };

  const handleRemove = async (): Promise<void> => {
    try {
      await API.entities.deletePipeline(EntityModules.Events, uuid)();
      notification.enqueueSuccessSnackbar(`Pipeline ${name} deleted`);
      await actions.refreshData();
    } catch (error) {
      const err = error as AxiosError;
      Logger.error('Failed to delete pipeline', err);

      const responseData = err.response?.data as string[] | undefined;
      if (Array.isArray(responseData) && responseData.length > 0) {
        responseData.forEach((message) => {
          notification.enqueueErrorSnackbar(message);
        });
      } else {
        notification.enqueueErrorSnackbar('Failed to delete pipeline');
      }
    }
  };

  const handleMarkAsDefault = async (): Promise<void> => {
    try {
      await API.entities.updatePipeline(EntityModules.Events, uuid, { default: true })();
      notification.enqueueSuccessSnackbar(`Default stage set to ${name}`);
      await actions.refreshData();
      handleClose();
    } catch (error) {
      Logger.error(error as string);
      notification.enqueueErrorSnackbar('Failed to mark pipeline as default');
    }
  };

  return (
    <>
      <GridActionsCellItem
        icon={<MoreVert />}
        label={moreActionsLabel(name)}
        color="inherit"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid="more-vert-button"
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem sx={{ padding: '6px 16px' }} id="edit-button" data-testid="edit-button" onClick={handleEditClick}>
          Edit
        </MenuItem>
        {!isDefault && (
          <>
            <MenuItem
              id="delete-button"
              data-testid="delete-button"
              sx={{ padding: '6px 16px' }}
              onClick={() => setOpenDeleteModal(true)}
            >
              Delete
            </MenuItem>
            <Divider sx={{ m: 0 }} />
            <MenuItem
              id="mark-as-default-button"
              onClick={handleMarkAsDefault}
              sx={{ padding: '6px 16px' }}
              data-testid="mark-as-default-button"
            >
              Mark as default
            </MenuItem>
          </>
        )}
      </Popover>
      {openDeleteModal && <DeleteEntityModal onClose={() => setOpenDeleteModal(false)} handleRemove={handleRemove} />}
    </>
  );
};

export default PipelinesActionCell;
